import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * Full-screen composition of Shopify search. Products, counts, suggestions and
 * facet values are server-owned; product cards keep Ritual's purchase components.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement} clear
 * @property {HTMLElement} results
 * @property {HTMLElement} scrollArea
 * @property {HTMLElement} suggestions
 * @property {HTMLElement} suggestionLinks
 * @property {HTMLElement} status
 * @property {HTMLElement} footer
 * @property {HTMLAnchorElement} viewAll
 * @property {HTMLTemplateElement} emptyState
 * @extends Component<Refs>
 */
class CatalogSearch extends Component {
  requiredRefs = ['input', 'clear', 'results', 'scrollArea', 'suggestions', 'suggestionLinks', 'status', 'footer', 'viewAll', 'emptyState'];
  #listeners;
  #request;
  #timer;
  #generation = 0;
  #resizeFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    const dialog = this.closest('dialog-component');
    dialog?.addEventListener(DialogOpenEvent.eventName, this.#onOpen, options);
    dialog?.addEventListener(DialogCloseEvent.eventName, this.#onClose, options);
    document.addEventListener('keydown', this.#shortcut, options);
    window.addEventListener('resize', this.#scheduleResize, options);
    window.visualViewport?.addEventListener('resize', this.#scheduleResize, options);
    window.visualViewport?.addEventListener('scroll', this.#scheduleResize, options);
    this.#resize();
    // A disconnected request cannot leave a reconnected dialog inert.
    if (this.refs.input.value.trim()) this.#load(this.#queryURL());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancel();
    this.#listeners?.abort();
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = 0;
  }

  #onOpen = () => {
    this.#resize();
    this.refs.scrollArea.scrollTop = 0;
    // Let touch visitors browse immediately, without opening the keyboard or focusing a text field.
    const target = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches
      ? this.refs.input
      : this.closest('dialog')?.querySelector('#catalog-search-heading');
    target?.focus({ preventScroll: true });
  };
  #onClose = () => {
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = 0;
    this.reset(false);
  };

  #scheduleResize = () => {
    if (this.#resizeFrame || !this.closest('dialog')?.open) return;
    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = 0;
      this.#resize();
    });
  };

  #resize = () => {
    if (!this.closest('dialog')?.open) return;
    this.style.setProperty('--catalog-search-height', `${window.visualViewport?.height ?? window.innerHeight}px`);
    this.style.setProperty('--catalog-search-top', `${Math.max(0, window.visualViewport?.offsetTop ?? 0)}px`);
  };

  /** @param {KeyboardEvent} event */
  #shortcut = (event) => {
    if (!(event.metaKey || event.ctrlKey) || typeof event.key !== 'string' || event.key.toLowerCase() !== 'k') return;
    event.preventDefault();
    this.closest('dialog-component')?.showDialog();
  };

  #cancel() {
    clearTimeout(this.#timer);
    this.#request?.abort();
    this.#generation++;
  }

  #queryURL() {
    const url = new URL(this.dataset.searchUrl, window.location.origin);
    url.searchParams.set('q', this.refs.input.value.trim());
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return url;
  }

  /** @param {InputEvent} event */
  search(event) {
    this.#cancel();
    if (event.isComposing) {
      this.#prepare(this.#queryURL());
      return;
    }
    if (!this.refs.input.value.trim()) {
      this.reset(false);
      return;
    }
    this.#prepare(this.#queryURL());
    this.#timer = setTimeout(() => this.#load(this.#queryURL()), 200);
  }

  /** @param {boolean | Event} [keepFocus] */
  reset(keepFocus = true) {
    this.#cancel();
    this.refs.input.value = '';
    this.refs.clear.hidden = true;
    this.refs.suggestions.hidden = true;
    this.refs.suggestionLinks.replaceChildren();
    this.refs.results.replaceChildren(this.refs.emptyState.content.cloneNode(true));
    this.refs.scrollArea.scrollTop = 0;
    this.refs.results.inert = false;
    this.refs.results.removeAttribute('aria-busy');
    this.refs.footer.hidden = true;
    this.refs.status.textContent = '';
    this.refs.status.classList.add('visually-hidden');
    this.removeAttribute('data-loading');
    if (keepFocus !== false) this.refs.input.focus({ preventScroll: true });
  }

  /** @param {URL} url */
  #prepare(url) {
    this.refs.clear.hidden = false;
    this.refs.results.inert = true;
    this.refs.results.setAttribute('aria-busy', 'true');
    this.refs.suggestions.hidden = true;
    this.refs.viewAll.href = url.toString();
    this.refs.viewAll.textContent = this.dataset.viewAllLabel;
    this.refs.footer.hidden = false;
    this.refs.status.textContent = this.dataset.loadingLabel;
    this.refs.status.classList.add('visually-hidden');
    this.setAttribute('data-loading', '');
  }

  /** Keep the currently applied filters when Enter opens the complete result page.
   * @param {SubmitEvent} event
   */
  submitSearch(event) {
    if (!this.refs.input.value.trim()) return;
    event.preventDefault();
    window.location.assign(this.refs.viewAll.href || this.#queryURL().toString());
  }

  /** @param {MouseEvent} event */
  chooseSuggestion(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
    const link = event.target instanceof Element ? event.target.closest('a[data-search-query]') : null;
    if (!link) return;
    event.preventDefault();
    this.refs.input.value = link.dataset.searchQuery ?? '';
    this.refs.input.focus({ preventScroll: true });
    this.#load(this.#queryURL());
  }

  /** @param {SubmitEvent} event */
  applyFilters(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    const data = new FormData(form);
    const priceInputs = [...form.querySelectorAll('[data-search-price]')];
    const prices = [];
    for (const input of priceInputs) {
      input.setCustomValidity('');
      if (!input.value.trim()) {
        data.delete(input.name);
        prices.push(null);
        continue;
      }
      const currency = form.dataset.currency;
      const amount = convertMoneyToMinorUnits(input.value, currency);
      if (amount === null || input.value.trim().startsWith('-')) {
        input.setCustomValidity(this.dataset.priceError);
        input.reportValidity();
        return;
      }
      prices.push(amount);
      data.set(input.name, formatMoney(amount, '{{amount}}', currency).replaceAll(',', ''));
    }
    if (prices.length === 2 && prices[0] !== null && prices[1] !== null && prices[0] > prices[1]) {
      priceInputs[1].setCustomValidity(this.dataset.priceError);
      priceInputs[1].reportValidity();
      return;
    }
    const url = this.#queryURL();
    for (const [name, value] of data) {
      if ((name.startsWith('filter.') || name === 'sort_by') && typeof value === 'string' && value !== '') {
        url.searchParams.append(name, value);
      }
    }
    this.#load(url, true);
  }

  /** Clear a previous native form error as either price is edited.
   * @param {InputEvent} event
   */
  clearPriceError(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    for (const input of event.target.form?.querySelectorAll('[data-search-price]') ?? []) input.setCustomValidity('');
  }

  /** @param {MouseEvent} event */
  clearFilters(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    this.#load(this.#queryURL(), true);
  }

  /** Native Tab navigation stays available for cards, filters and purchase forms.
   * @param {KeyboardEvent} event
   */
  focusSuggestions(event) {
    if (event.key !== 'ArrowDown' || this.refs.suggestions.hidden) return;
    const link = this.refs.suggestionLinks.querySelector('a');
    if (!link) return;
    event.preventDefault();
    link.focus();
  }

  /** @param {URL} url @param {boolean} [restoreFilterFocus] */
  async #load(url, restoreFilterFocus = false) {
    this.#cancel();
    const generation = this.#generation;
    const controller = new AbortController();
    this.#request = controller;
    const current = () => this.isConnected && !controller.signal.aborted && generation === this.#generation;
    this.#prepare(url);
    const deferred = SearchUpdateEvent.createPromise();
    // A canceled search is expected, including when no analytics consumer is listening.
    deferred.promise.catch(() => {});
    this.dispatchEvent(new SearchUpdateEvent({ search: { query: url.searchParams.get('q') }, promise: deferred.promise }));
    void this.#loadSuggestions(url.searchParams.get('q'), controller.signal, current);
    try {
      // The renderer adds section_id; keep the full-page navigation URL untouched.
      const markup = await sectionRenderer.getSectionHTML('catalog-search', false, new URL(url), controller.signal);
      if (!current()) throw new DOMException('Search superseded', 'AbortError');
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const fragment = parsed.querySelector('[data-catalog-search-results]');
      const count = Number(fragment?.dataset.resultCount);
      if (!fragment || !Number.isSafeInteger(count) || count < 0) throw new Error('Search response is unavailable');
      const container = document.createElement('div');
      // Section-rendered CSS is needed even if the initial catalog was empty.
      for (const style of parsed.querySelectorAll('style[data-section-stylesheet]')) container.append(style.cloneNode(true));
      container.append(fragment);
      await morph(this.refs.results, container);
      if (!current()) throw new DOMException('Search superseded', 'AbortError');
      const grid = this.refs.results.querySelector('[ref="grid"]');
      for (const radio of this.refs.results.querySelectorAll('[name="catalog-search-grid-mobile"]')) {
        radio.checked = radio.value === (grid?.getAttribute('product-grid-view') ?? 'default');
      }
      this.refs.viewAll.href = url.toString();
      this.refs.viewAll.textContent = fragment.dataset.viewAllLabel;
      this.refs.status.textContent = fragment.dataset.statusLabel;
      this.refs.scrollArea.scrollTop = 0;
      if (restoreFilterFocus) this.refs.results.querySelector('[data-search-filters-summary]')?.focus({ preventScroll: true });
      deferred.resolve({ totalCount: count });
    } catch (error) {
      deferred.reject(error);
      if (!current()) return;
      this.refs.results.replaceChildren();
      this.refs.status.textContent = this.dataset.errorLabel;
      this.refs.status.classList.remove('visually-hidden');
      // The real GET search remains reachable even if section rendering fails.
      this.refs.viewAll.href = url.toString();
      this.refs.viewAll.textContent = this.dataset.viewAllLabel;
    } finally {
      if (current()) {
        this.refs.results.inert = false;
        this.refs.results.removeAttribute('aria-busy');
        this.removeAttribute('data-loading');
      }
    }
  }

  /** @param {string} query @param {AbortSignal} signal @param {() => boolean} current */
  async #loadSuggestions(query, signal, current) {
    try {
      const url = new URL(Theme.routes.predictive_search_url, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'query');
      url.searchParams.set('resources[limit]', '6');
      const markup = await sectionRenderer.getSectionHTML('predictive-search', false, url, signal);
      if (!current()) return;
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const links = [...parsed.querySelectorAll('.predictive-search-results__wrapper-queries a')];
      const nodes = links.map((source) => {
        const link = document.createElement('a');
        const term = source.getAttribute('aria-label') || source.textContent.trim();
        const target = new URL(this.dataset.searchUrl, window.location.origin);
        target.searchParams.set('q', term);
        target.searchParams.set('type', 'product');
        target.searchParams.set('options[prefix]', 'last');
        link.href = target.toString();
        link.textContent = term;
        link.dataset.searchQuery = term;
        link.setAttribute('on:click', 'catalog-search-component/chooseSuggestion');
        return link;
      });
      this.refs.suggestionLinks.replaceChildren(...nodes);
      this.refs.suggestions.hidden = nodes.length === 0;
    } catch {
      // Predictive queries are optional; full search results remain authoritative.
      if (current()) this.refs.suggestions.hidden = true;
    }
  }
}

if (!customElements.get('catalog-search-component')) customElements.define('catalog-search-component', CatalogSearch);

